"use client";

import React, { useState, useEffect } from "react";
import { Search, CheckCircle, XCircle, Globe, ArrowRight } from 'lucide-react';

export interface DomainPrice {
    tld: string;
    price: number | null;
}

interface DomainCheckResult {
    available: boolean;
    message: string;
    buyLink?: string | null;
}

interface DomainSearchProps {
    initialPrices?: DomainPrice[] | null;
}

export default function DomainSearch({ initialPrices = null }: DomainSearchProps) {
    const [domain, setDomain] = useState("");
    const [searchResult, setSearchResult] = useState<DomainCheckResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [domainPrices, setDomainPrices] = useState<DomainPrice[] | null>(initialPrices);
    const [pricesLoading, setPricesLoading] = useState(!initialPrices);

    useEffect(() => {
        if (initialPrices) return; // Skip if we already have server data

        const controller = new AbortController();

        async function fetchPrices() {
            setPricesLoading(true);
            try {
                const res = await fetch("/api/domain-prices", { signal: controller.signal });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: DomainPrice[] = await res.json();
                setDomainPrices(data);
            } catch (err) {
                if (err instanceof Error && err.name !== 'AbortError') {
                    setDomainPrices(null);
                }
            } finally {
                setPricesLoading(false);
            }
        }

        void fetchPrices();

        return () => controller.abort();
    }, [initialPrices]);

    const handleSearch = async () => {
        if (!domain.trim()) return;
        setIsLoading(true);
        setSearchResult(null);

        try {
            const response = await fetch(`/api/domain-check?domain=${encodeURIComponent(domain.trim())}`);
            const data: DomainCheckResult = await response.json();
            setSearchResult({
                available: data.available,
                message: data.message,
                buyLink: data.buyLink || null,
            });
        } catch {
            setSearchResult({ available: false, message: "Connection error. Try again.", buyLink: null });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-16">
            <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-emerald-500/10 text-emerald-800 text-[11px] font-mono font-semibold uppercase tracking-wider mb-5 border border-emerald-500/20 shadow-xs">
                    <Globe size={12} className="text-emerald-600" />
                    Domain Registration
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">
                    Find Your Perfect <span className="gradient-text">Domain</span>
                </h2>
                <p className="text-slate-600 font-medium max-w-lg mx-auto">Secure your brand with the perfect domain name at competitive prices.</p>
            </div>

            {/* Search Bar */}
            <div className="relative group max-w-2xl mx-auto mb-12">
                {/* Glow ring on focus */}
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20 rounded-xl blur-md opacity-0 group-focus-within:opacity-100 transition-all duration-500" />

                <div className="relative flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden p-1.5 shadow-xs focus-within:border-green-400 focus-within:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all duration-300">
                    <div className="pl-4 text-slate-400">
                        <Globe size={18} />
                    </div>
                    <input
                        type="text"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="example.com"
                        className="w-full bg-transparent px-4 py-3 text-slate-800 outline-none placeholder:text-slate-400 font-medium text-[15px]"
                    />
                    <button
                        onClick={handleSearch}
                        disabled={isLoading || !domain}
                        aria-label="Search Domain"
                        className="bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white font-bold px-6 py-2.5 rounded-lg transition-all flex items-center gap-2 disabled:bg-slate-100 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 shadow-sm shadow-green-500/15 active:scale-95"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <Search className="text-sm" aria-hidden="true" />
                                <span className="hidden sm:inline">Search</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Search Result */}
            {searchResult && (
                <div className={`mb-12 p-5 rounded-xl border-2 transition-all duration-300 ${
                    searchResult.available
                        ? "bg-green-50/80 border-green-200 text-green-800"
                        : "bg-red-50/80 border-red-200 text-red-800"
                }`}>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            {searchResult.available ? (
                                <CheckCircle className="text-green-500" size={22} />
                            ) : (
                                <XCircle className="text-red-500" size={22} />
                            )}
                            <span className="font-bold text-lg">{searchResult.message}</span>
                        </div>
                        {searchResult.available && searchResult.buyLink && (
                            <a
                                href={searchResult.buyLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto px-8 py-2.5 bg-gradient-to-r from-green-600 to-emerald-500 text-white font-bold rounded-lg hover:shadow-md hover:shadow-green-500/20 hover:-translate-y-0.5 transition-all text-center active:scale-95"
                            >
                                Register Now
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Domain Prices Grid */}
            <div>
                <div className="flex items-center gap-4 mb-8">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-slate-200" />
                    <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-600">Popular Extensions</h3>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
                </div>

                {pricesLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-40 bg-slate-100/80 rounded-2xl animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                        {domainPrices?.map(({ tld, price }) => (
                            <div
                                key={tld}
                                className="group relative p-6 bg-white border border-slate-100 rounded-2xl hover:border-green-300/60 hover:shadow-[0_8px_30px_-8px_rgba(16,185,129,0.15)] transition-all duration-400 overflow-hidden"
                            >
                                {/* Subtle hover background */}
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/0 to-green-50/0 group-hover:from-green-50/50 group-hover:to-emerald-50/30 transition-all duration-500 pointer-events-none" />

                                <div className="relative">
                                    <div className="absolute top-0 right-0 text-slate-200 group-hover:text-green-400 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                                        <ArrowRight size={11} />
                                    </div>
                                    <h4 className="text-2xl font-extrabold text-slate-800 mb-1 tracking-tight">{tld}</h4>
                                    <p className="text-green-700 font-bold text-lg mb-4">
                                        ৳{price ?? "N/A"}<span className="text-slate-400 text-xs font-medium ml-1">/year</span>
                                    </p>
                                    <a
                                        href={`https://billing.banglaverse.net/cart.php?a=add&domain=register&tld=${encodeURIComponent(tld.replace(".", ""))}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-full text-center py-2.5 rounded-xl bg-slate-50 text-slate-600 text-xs font-bold group-hover:bg-gradient-to-r group-hover:from-green-600 group-hover:to-emerald-500 group-hover:text-white transition-all duration-300"
                                    >
                                        Get Started
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
